import PropTypes from 'prop-types';
import {
  Box, Typography, Chip,
  TableBody, TableRow, TableCell, TablePagination,
  Alert,
} from '@mui/material';
import { WhatsApp as WhatsAppIcon } from '@mui/icons-material';
import StandardCard from '../../components/ui/StandardCard';
import StandardTable from '../../components/ui/StandardTable';

const DIRECTION_LABEL = { outbound: 'Enviada', inbound: 'Recebida' };
const PURPOSE_LABEL = {
  created: 'Agendamento criado',
  confirmation_request: 'Confirmação',
  reminder: 'Lembrete',
  cancelled: 'Cancelamento',
  reply: 'Resposta',
};

const STATUS_COLOR = {
  sent:        'info',
  delivered:   'success',
  read:        'success',
  failed:      'error',
  undelivered: 'error',
  received:    'default',
};

const messageColumns = [
  { id: 'createdAt',   label: 'Data/Hora' },
  { id: 'direction',   label: 'Direção' },
  { id: 'purpose',     label: 'Tipo' },
  { id: 'status',      label: 'Status', align: 'center' },
  { id: 'phoneMasked', label: 'Telefone' },
];

function formatDateTime(isoStr) {
  if (!isoStr) return '—';
  return new Date(isoStr).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

/**
 * MessageHistoryCard — Histórico de mensagens WhatsApp com paginação.
 * Estado vazio compacto (sem o vazio gigante do EmptyState).
 */
export default function MessageHistoryCard({
  messages, total, page, rowsPerPage,
  onPageChange, onRowsPerPageChange,
  loading, error, setError, summary,
}) {
  const isEmpty = !loading && total === 0 && !error;

  return (
    <StandardCard fillHeight={false}>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Typography variant="h6">Histórico de mensagens</Typography>
        {summary && total > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, ml: { sm: 'auto' } }}>
            <Chip size="small" color="success" variant="outlined" label={`${summary.sent} enviadas`} />
            {summary.failedNoCredit > 0 && (
              <Chip size="small" color="error" variant="outlined" label={`${summary.failedNoCredit} sem saldo`} />
            )}
            <Chip size="small" variant="outlined" label={`${summary.received} recebidas`} />
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {isEmpty ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <WhatsAppIcon sx={{ fontSize: 40, color: 'text.disabled', opacity: 0.5, mb: 1 }} />
          <Typography variant="body2" color="text.secondary">Nenhuma mensagem ainda.</Typography>
          <Typography variant="caption" color="text.secondary">
            As mensagens enviadas e recebidas vão aparecer aqui.
          </Typography>
        </Box>
      ) : (
        <>
          <StandardTable
            columns={messageColumns}
            loading={loading}
            isEmpty={messages.length === 0 && !loading}
            emptyMessage="Nenhuma mensagem nesta página."
          >
            <TableBody>
              {messages.map((msg, idx) => (
                <TableRow key={idx} hover>
                  <TableCell>{formatDateTime(msg.createdAt)}</TableCell>
                  <TableCell>{DIRECTION_LABEL[msg.direction] ?? msg.direction}</TableCell>
                  <TableCell>{PURPOSE_LABEL[msg.purpose] ?? msg.purpose ?? '—'}</TableCell>
                  <TableCell align="center">
                    <Chip label={msg.status} color={STATUS_COLOR[msg.status] ?? 'default'} size="small" />
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', letterSpacing: 1 }}>
                    {msg.phoneMasked}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </StandardTable>

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={onPageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={onRowsPerPageChange}
            rowsPerPageOptions={[10, 20, 50]}
            labelRowsPerPage="Por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          />
        </>
      )}
    </StandardCard>
  );
}

MessageHistoryCard.propTypes = {
  messages: PropTypes.array.isRequired,
  total: PropTypes.number.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  setError: PropTypes.func.isRequired,
  summary: PropTypes.object,
};
